import { createRouter } from "@tanstack/solid-router";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { todosRoute } from "@/routes/_app/todos";
import { myDayRoute } from "@/routes/_app/my-day";
import { importantRoute } from "@/routes/_app/important";
import { plannedRoute } from "@/routes/_app/planned";
import { inboxRoute } from "@/routes/_app/inbox";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { listRoute } from "@/routes/_app/lists.$listId";
import { listTodoRoute } from "@/routes/_app/lists.$listId.todo.$todoId";
import { inboxTodoRoute } from "@/routes/_app/inbox.todo.$todoId";
import { importantTodoRoute } from "@/routes/_app/important.todo.$todoId";
import { myDayTodoRoute } from "@/routes/_app/my-day.todo.$todoId";
import { allTodoRoute } from "@/routes/_app/todos.todo.$todoId";
import { plannedTodoRoute } from "@/routes/_app/planned.todo.$todoId";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { adminRoute } from "@/routes/_app/admin";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminAuditRoute } from "@/routes/_app/admin.audit";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { globalTodoRoute } from "@/routes/_app/todo.$todoId";
import { sandboxModalRoute } from "@/routes/_app/modal";

const routeTree = rootRoute.addChildren([
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Dashboard
    todosRoute.addChildren([allTodoRoute]), // path: "/all"
    myDayRoute.addChildren([myDayTodoRoute]),
    importantRoute.addChildren([importantTodoRoute]),
    plannedRoute.addChildren([plannedTodoRoute]),
    inboxRoute.addChildren([inboxTodoRoute]),
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    adminRoute.addChildren([
      adminStorageRoute,
      adminAuditRoute,
      adminThemeAuditRoute,
      adminThemesRoute,
      adminFontsRoute,
    ]),
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    globalTodoRoute,
    sandboxModalRoute,
    debugRoute,
    listRoute.addChildren([listTodoRoute]),
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
